/**
  * @class mmodOfficeWfDopplers
  * @description
  *
  * # Purpose
  * Module displays Doppler radar measurements imported from vehicles.
  * Also allows import of new measurements.
  */

import mmodWorkflow from 'mmodWorkflow'
const mfs = require('msysFS')
const Doppler = require('../../msvc/msvcDoppler/doppler')

export default class mmodOfficeWfDopplers extends mmodWorkflow {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'full' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      workflow_server: {
        type: 'string',
        default: ':msvrWfDopplers',
        description: 'Listener acting as the server-side for the workflow',
      },
      tabular: {
        type: 'boolean',
        default: true,
      },
      delay_initial_search: {
        type: 'integer',
        description: 'delay of initial search',
        default: 20,
      },
      imprint_video: {
        type: 'object',
        description: 'imprint video configuration',
        properties: {
          speed: {
            type: 'number',
            description: 'playback speed of MP4',
            default: 0.25,
          },
          gpu: {
            type: 'string',
            enum: ['', 'cuda', 'd3d11va', 'dxva2', 'qsv', 'vaapi', 'vdpau', 'videotoolbox'],
          },
          preset: {
            type: 'string',
            enum: ['ultrafast', 'superfast', 'veryfast', 'faster', 'fast', 'medium', 'slow', 'slower', 'veryslow', 'placebo'],
          },
          quality: {
            type: 'integer',
            description: 'video quality for imprinting',
            minimum: 0,
            maximum: 51,
          },
        },
        default: {
          gpu: '',
          preset: 'veryfast',
          quality: 23,
        },
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)

    const controls = ['corners', 'mid', 'travel', 'alpr']
    const controls_on = mfs.getJSON('mmodOfficeWfDopplers_controls') || { corners: 1, mid: 1 }
    this.controls = {}
    for(const key of controls) {
      this.controls[key] = controls_on?.[key] ? 1 : 0
    }
    mfs.setJSON('mmodOfficeWfDopplers_controls', this.controls)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  async mmFormSubmit(sender, params) {
    const { button } = params
    if(button==='window_print') {
      window.print()
    }else if(button==='import_file') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: false,
        selected: ['mmDopplerHit'],
      })
    }else if(button==='import_folder') {
      this.publish('mmodOfficeImport', 'mmImportPrompt', {
        folders: true,
        selected: ['mmDopplerHit'],
      })
    }else if(button==='ACTION:EXPORT') {
      this.exportLocal(params.datakey)
    }else if(button==='ACTION:EXPORT_TRM') {
      this.exportTrm(params.datakey)
    }else if(button==='ACTION:VIDEO') {
      this.exportVideo(params.datakey)
    }else{
      super.mmFormSubmit(sender, params)
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * opens file selection dialog for user to select files for import
  */
  async exportLocal(id) {
    try{
      const reply = await this.request(':msvrWfDopplers', 'mmAction', {
        action: 'EXPORT',
        id,
      })
      const binary = reply?.params?.list?.[0]?.export
      if(binary && binary.length>0) {
        const blob = new Blob([binary], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = id+'.mm'
        a.click()
        URL.revokeObjectURL(url)
      }else {
        this.modalError('wf.dopplers.export.NoData', 'wf.dopplers.export.Error')
      }
    }catch(e) {
      this.modalError(String(e), 'wf.dopplers.export.Error')
    }
  } // exportLocal

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * *Export to TRM*: hands the operator the request body IS TM would be given for this measurement.
  *
  * Separate from exportLocal because what comes back is not the same kind of thing. That one saves
  * the signed message the radar produced, as bytes, named `.mm`; this saves text - the batch request
  * as it would go on the wire - and text wants to open in a viewer, so it is `application/json` and
  * named `.json`.
  *
  * The workflow answers a failure in the very field the file would arrive in (a translation key
  * instead of bytes), which is why a string is checked for rather than assumed away: without it the
  * operator would be handed a file containing the words "export failed".
  * @param {string} id - measurement record ID
  */
  async exportTrm(id) {
    try{
      const reply = await this.request(':msvrWfDopplers', 'mmAction', {
        action: 'EXPORT_TRM',
        id,
      })
      const body = reply?.params?.list?.[0]?.export
      if(body && body.length > 0 && typeof body !== 'string') {
        const blob = new Blob([body], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = id + '.json'
        a.click()
        URL.revokeObjectURL(url)
      }else {
        this.modalError(body || 'wf.dopplers.export.NoData', 'wf.dopplers.export.Error')
      }
    }catch(e) {
      this.modalError(String(e), 'wf.dopplers.export.Error')
    }
  } // exportTrm

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * opens file selection dialog for user to select files for import
  */
  async exportVideo(id) {
    const form = this.el.find('mele-view[key=read][role*=bottom]')
    const data = form?.record_data
    if(!data) return
    const language = this.el.language
    this.modalWait('{{Please wait}}...', 'wf.dopplers.Export')
    try{
      const reply = await this.request(':msvrWfDopplers', 'mmAction', {
        action: 'VIDEO',
        id,
        params: {
          ...this.config.imprint_video,
          language,
          corners: this.controls.corners ? '12%' : null,
          mid: !!this.controls.mid,
          travel: !!this.controls.travel,
        },
      })
      const binary = reply?.params?.list?.[0]?.export
      if(binary && binary.length>0) {
        const blob = new Blob([binary], { type: 'video/mp4' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = this.exportFileName(data)+'.mp4'
        a.click()
        URL.revokeObjectURL(url)
        this.modalCloseDefault()
      }else {
        this.modalError('wf.dopplers.export.NoData', 'wf.dopplers.export.Error')
      }
    }catch(e) {
      this.modalError(String(e), 'wf.dopplers.export.Error')
    }
  } // exportVideo

  async refreshImage(form, artefacts) {
    const data = form?.record_data
    if(!data) return
    const img = form?.find('img.burned')
    if(!img) return
    const q = 0.9
    const params = this.reconstructEventParams(data)
    img.src = ''
    const src = (await Doppler.getEventImage({ action: 'mmDopplerHit', params }, artefacts, this.el.language, q))?.src
    if(src) {
      img.src = src
      img.onclick = this.showEnlargedImage.bind(this, img)
    }else {
      img.onclick = null
    }
  } // refreshImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * called on form_ready hook of detail form
  */
  async formready(form_name, record, form) {
    if(form_name === 'read') {
      // set state of image enhancement controls
      for(const key in this.controls) {
        const el = form.find('mele-input[key=control_'+key+']')
        if(el) {
          el.value = this.controls[key]
        }
      }
      form.record_data = record
      this.refreshImage(form, this.controls)
    }else if(form_name === 'print') {
      const issuer = form.find('mele-input[key=issuer]')
      if(issuer) issuer.value = this.user_id
      const issued = form.find('mele-input[key=issued]')
      if(issued) issued.value = Date.now()
      form.record_data = record
      this.refreshImage(form, { ...this.controls, alpr: false })
    }
  } // formready

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to set language
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.lang] - desired language in 2 character ISO 639-1
  */
  mmSetLanguage(sender, params) {
    // if language changed, update image on forms
    super.mmSetLanguage(sender, params)

    const form_read = this.el.find('mele-view[key=read]')
    if(form_read) {
      this.refreshImage(form_read, this.controls)
    }

    const form_print = this.el.find('mele-view[key=print]')
    if(form_print) {
      this.refreshImage(form_print, this.controls)
    }
  } // mmSetLanguage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * reconstruct structured event parameters from flat DB fields
  */
  reconstructEventParams(data) {
    if(!data || typeof data !== 'object') return {}
    data = { ...data } // clone to avoid modifying original data
    for(const key in data) {
      const match = /^(identity|calibration|optics|limits|event|alpr)_(.*)/.exec(key)
      if(match) {
        const [, domain, field] = match

        const property = this._schema_types['doppler_'+domain]?.properties?.[field]
        if(!property) continue // skip if no such property found

        const type = property?.type
        let value = data[key]
        if(type === 'integer') {
          value = parseInt(value)
          if(isNaN(value)) continue
        }else if(type === 'number') {
          value = parseFloat(value)
          if(isNaN(value)) continue
        }

        delete data[key] // remove flat fields
        if(!data[domain]) {
          data[domain] = {} // ensure domain exists
        }
        data[domain][field] = value // reconstruct structured field
      }
    }

    const properties = this._schema_actions.mmDopplerHit.properties || {}
    for(const key in properties) {
      if(key in data) {
        const type = properties[key]?.type
        let value = data[key]
        if(type === 'integer') {
          value = parseInt(value)
          if(!isNaN(value)) data[key] = value
        }else if(type === 'number') {
          value = parseFloat(value)
          if(!isNaN(value)) data[key] = value
        }
      }
    }

    return data
  } // reconstructEventParams

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * show enlarged canvas with doppler image
  */
  showEnlargedImage(source_img) {
    this.modalOpen({
      key: 'mmodOfficeWfDopplersPreview',
      position: 'center',
      outclick: ':modal.cancel',
      inclick: ':modal.cancel',
      class: 'modal-preview',
      items: [
        { tag: 'img', src: source_img.src },
      ],
    })
  } // showEnlargedImage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sends by &lt;mele-input> to parent module when input value changed
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.holder] - key attribute of input's parent container
   * @param {string} params.input - key attribute of the input element changed
   * @param {string} [params.value] - new value
   * @param {string} [params.old] - old value
   * @param {boolean} [params.invalid] - if true, indicates the value is not according to given format
   * @param {boolean} [params.interactive] - indicates if this was user activity
   */
  mmValueChanged(sender, params) {
    const { input, value, interactive } = params
    if(/^control_/.test(input) && interactive) {
      const ctrl = input.replace(/^control_/, '')
      this.controls[ctrl] = value > 0 ? 1 : 0
      mfs.setJSON('mmodOfficeWfDopplers_controls', this.controls)
      this.refreshImage(this.el.find('mele-view[key=read]'), this.controls)
    }
  } // mmValueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
   * @description
   * Sends by &lt;mele-button> to parent module when a button is clicked
   * @param {object} sender - message sender
   * @param {object} params - message parameters
   * @param {string} [params.holder] - key attribute of button's parent container
   * @param {string} params.button - key attribute of the button
   * @param {integer} [params.count] - number of times button was clicked
   * @param {boolean} [params.long] - shows if press was long or not
   */
  mmButtonClicked(sender, params) {
    const { button } = params
    if(button === 'snapshot_download') {
      const form = this.el.find('mele-view[key=read][role*=bottom]')
      if(form && form.record_data) {
        const img = form.find('img.burned')
        if(img && img.src) {
          const el = document.createElement('a')
          el.style.display = 'none'
          el.setAttribute('href', img.src)
          el.setAttribute('download', this.exportFileName(form.record_data) + '.jpg')
          document.body.appendChild(el)
          el.click()
          document.body.removeChild(el)
        }
      }
    }else{
      super.mmButtonClicked(sender, params)
    }
  } // mmButtonClicked

  exportFileName(data) {
    const type = 'hit'
    const sn = data?.identity_sn || 'unknownsn'
    const channel = data?.channel || 'unknownchannel'

    // for Doppler hits we also include sequential number
    let seq = parseInt(data?.seq)
    if(seq > 0) {
      seq = String(seq).padStart(6, '0')
    }else {
      seq = seq || 'unknownseq'
    }

    const jpeg_us = parseInt(data?.jpeg_us)
    const event_dt = parseInt(data?.event_dt)
    if(!(event_dt > 0)) return

    const d = new Date((jpeg_us>0 ? jpeg_us : event_dt)/1000)
    const dt_str =
      String(d.getFullYear()).padStart(4, 0)+'-' +
      String(d.getMonth()+1).padStart(2, 0)+'-' +
      String(d.getDate()).padStart(2, 0)+'_' +
      String(d.getHours()).padStart(2, 0)+'-' +
      String(d.getMinutes()).padStart(2, 0)+'-' +
      String(d.getSeconds()).padStart(2, 0)
    const filename = `mosy_doppler_${type}_${sn}_${channel}_${event_dt}_${seq}_${dt_str}`
    return filename
  } // exportFileName
} // mmodOfficeWfDopplers
